import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import Workflow, { WorkflowStatus } from 'models/Workflow';
import { indexWorkflows, WorkflowListParams, workflowQueryKeys } from 'services/api/workflows';

import { PORTAL_AUTOMATION_API_NAME } from '../constants';

// The strip heals through the same nudge as the lists — this poll is the dropped-socket
// backstop, shorter than the tasks list's because a running strip that lies is worse than
// an attention list that lags: the runs it shows are actively changing state.
const FALLBACK_POLL_INTERVAL_MS = 60_000;

// A strip, not a list: no paging, no sort control. Twenty is far above any plausible number
// of simultaneously running headless retrievals for one client.
const STRIP_PAGE_SIZE = 20;

const RUNNING_PARAMS: WorkflowListParams = {
  definitionApiName: [PORTAL_AUTOMATION_API_NAME],
  status: [WorkflowStatus.RUNNING],
  sortBy: 'created_at desc',
};

/**
 * The runs in flight that nobody drives: `portal_automation` in `running`, headless mode only.
 * The Tasks list deliberately excludes these (a run in flight requires nobody's attention), but
 * a headless dispatch makes work VANISH from that list with nothing saying where it went — this
 * is the "where it went".
 *
 * Status is filtered server-side like the tasks query; mode is filtered HERE because the index
 * has no retrieval-mode parameter — it serializes `retrieval_mode` onto each portal row instead.
 * That's affordable where the tasks filter wasn't: `running` is a moment, not a lifetime, so the
 * unfiltered page stays small.
 *
 * Keyed under the shared `['workflows','list']` prefix on purpose: the WorkflowListChannel nudge
 * (subscribed once per page, in useTasks) invalidates that prefix, so a run starting or settling
 * refreshes this strip with no subscription of its own.
 */
export default function useRunningHeadless() {
  const query = useQuery({
    queryKey: workflowQueryKeys.list(RUNNING_PARAMS),
    queryFn: ({ signal }) => indexWorkflows({ ...RUNNING_PARAMS, page: 1, pageSize: STRIP_PAGE_SIZE }, signal),
    refetchInterval: FALLBACK_POLL_INTERVAL_MS,
  });

  // Status re-checked on the row even though the request filters on it: the strip's one promise
  // is that nothing settled ever shows as running, so a stale page must fail closed.
  const workflows = useMemo(
    () =>
      (query.data?.data ?? []).filter(
        (workflow: Workflow) => workflow.status === WorkflowStatus.RUNNING && workflow.retrievalMode === 'headless'
      ),
    [query.data]
  );

  return { workflows, isLoading: query.isPending };
}
